/**
 * Extracts the backend's error message from an axios error. When a request was
 * made with responseType 'blob' or 'arraybuffer' the JSON error body arrives as
 * binary, so it has to be decoded first (otherwise users only ever see a
 * generic failure message, even for "file too large" style errors).
 */
export const readApiErrorMessage = async (err: unknown, fallback: string): Promise<string> =>
  (await readApiError(err, fallback)).message;

/** Same decoding as readApiErrorMessage, also returning the machine readable error code. */
export const readApiError = async (
  err: unknown,
  fallback: string
): Promise<{ message: string; code?: string }> => {
  const response = (err as { response?: { status?: number; data?: unknown } } | null)?.response;
  if (!response) return { message: fallback };

  let data = response.data;
  try {
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
      data = JSON.parse(await data.text());
    } else if (data instanceof ArrayBuffer) {
      data = JSON.parse(new TextDecoder().decode(data));
    }
  } catch {
    data = undefined;
  }

  const apiError = (data as { error?: { message?: string; code?: string } } | undefined)?.error;
  if (apiError?.message) return { message: apiError.message, code: apiError.code };
  if (response.status === 413) return { message: 'The file is too large to upload.', code: 'FILE_TOO_LARGE' };
  return { message: fallback, code: apiError?.code };
};
